// Copyright (c) 2026 Michael F. Kamprath
//
// cloud-spawner.js -- ambient cloud scroll layer for themes that have
// a `cloudZoneRect` + `cloudImageNames` in themes.json.  Enabled for
// cows, chicks, and flowers per the legacy plist (apples / horses /
// sheep have no cloud config and get a no-op spawner).
//
// Design goals:
//   1. Timer-driven spawning matching the legacy Obj-C's formula so
//      cloud cadence feels identical to the iPad app:
//      minDelay = (zoneWidth / 3) / speed   (seconds)
//      nextDelay = minDelay + rng()*100 * 0.3     (0..30 s jitter)
//   2. Right-to-left scroll on a per-cloud timeline -- each cloud
//      travels from (zone.rightEdge + cloudWidth) to (zone.leftEdge -
//      cloudWidth), durationSec = travelDistance / speed.
//   3. Random Y offset within the zone + random image from the
//      theme's `cloudImageNames`.
//   4. Cleanup on destroy() -- cancels the pending spawn timer AND
//      the in-flight scroll animations, removes all spawned sprites
//      from the container so the scene tears down without sprite
//      leaks.
//
// Slice 2.5.2 adds the cloud-tap reaction: thunder SFX, a whole-
// screen white flash, and (for themes whose plist listed lightning
// bolt art -- chicks only per the legacy) a bolt sprite overlaid on
// the tapped cloud for the duration of the thunder clip.  Cows and
// flowers get the SFX + flash but no bolt sprite (the legacy plist
// sets `lightningImageNames` to an empty array for those themes).

import * as PIXI from 'pixi.js';
import { animateOver } from './entrance.js';
import { createAlphaMaskFromUrl } from './alpha-mask.js';

const DEFAULT_DEPS = Object.freeze({
  setTimeoutImpl: (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl: (id) => clearTimeout(id),
  rng: Math.random,
  // 2.5.2 optional hooks.  Left no-op by default so tests that built
  // a spawner without the cloud-tap plumbing continue to work.
  playThunder: null,        //  () => Promise<void>    audio clip
  triggerScreenFlash: null, //  (durationMs) => void   flash overlay
  onSideGameTap: null,      //  () => void             telemetry hook
  // Pixel-perfect hit-test factory: `(url) => Promise<AlphaMask|null>`.
  // Returning null disables per-pixel testing so cloud sprites fall
  // back to their default rectangular hit area.  Tests override with
  // a stub returning synthetic masks so jsdom doesn't need to load
  // real images.
  buildAlphaMask: createAlphaMaskFromUrl,
});

// Hit-test thresholds (alpha 0..255).
//   CLOUD_ALPHA_MIN     : below this the pixel counts as transparent,
//                         so a tap there isn't "on" the cloud.  Set
//                         just above zero so the soft feathered edge
//                         of the cloud silhouette is still tappable
//                         while rejecting the deadest edge pixels
//                         where the sampled mask reads 1 from
//                         bilinear downsample noise rather than
//                         actual cloud coverage.
//   BACKDROP_ALPHA_MAX  : at or above this the backdrop is opaque enough
//                         to completely hide the cloud, so a tap there
//                         really targets the backdrop (tree, barn, fence).
//                         Anti-aliased edges (alpha < 200) still let
//                         the tap through to the cloud beneath.
const CLOUD_ALPHA_MIN = 8;
const BACKDROP_ALPHA_MAX = 200;

/**
 * Build a hitArea object for a cloud sprite whose `contains(x, y)`
 * returns true only when a tap at sprite-local (x, y) lands on a
 * visible cloud pixel AND none of the supplied occluder masks are
 * opaque at the same world point.  Uses the sprite's current x/y
 * at call time, so a scrolling cloud's occlusion check tracks its
 * position naturally.
 *
 * @param {PIXI.Sprite} sprite
 * @param {import('./alpha-mask.js').AlphaMask | null} cloudMask
 * @param {Array<import('./alpha-mask.js').AlphaMask | null>} occluderMasks
 *   Masks of display-list layers that visually hide the cloud --
 *   the theme's backdrop (trees, barn, fence) and any overlay
 *   layer.  Nulls in the list are ignored (failed loads).
 * @param {number} designW
 * @param {number} designH
 * @returns {{ contains(x: number, y: number): boolean }}
 */
function makeCloudHitArea(sprite, cloudMask, occluderMasks, designW, designH) {
  return {
    contains(x, y) {
      // IMPORTANT: in PixiJS v8, `hitArea.contains(x, y)` receives
      // coordinates in the sprite's TEXTURE-NATIVE space (bounds
      // 0..texture.width x 0..texture.height) -- the renderer
      // inverse-transforms the world point through the sprite's
      // worldTransform, which undoes its scale.  So we compare
      // bounds and compute UVs against the texture's raw pixel
      // dimensions, NOT the scaled `sprite.width` / `sprite.height`
      // that reflect the displayed size.  Using displayed size
      // rejected the right-half and bottom-half of every cloud
      // (the user-reported "lower half not clickable" bug).
      const texture = sprite.texture;
      const nativeW = texture?.width || sprite.width;
      const nativeH = texture?.height || sprite.height;

      if (x < 0 || y < 0 || x > nativeW || y > nativeH) return false;

      // Reject transparent pixels in the cloud texture itself.  If
      // the cloud mask failed to build (null), skip this check so
      // the sprite stays fully tappable rather than silently dead.
      if (cloudMask) {
        const cu = x / nativeW;
        const cv = y / nativeH;
        if (cloudMask.getAlphaAt(cu, cv) < CLOUD_ALPHA_MIN) return false;
      }

      // Reject points where ANY occluder layer (backdrop or overlay
      // -- trees, barn, fence, foreground props) is opaque enough
      // to hide the cloud visually.  Each layer is sized to the full
      // design rect, so the world point maps directly to the mask's
      // normalized UV.  To go from texture-native local -> world,
      // we scale through the sprite's display/native ratio, which
      // equals the sprite's worldTransform scale for flat parent
      // hierarchies (our scene is just sceneRoot with no transforms).
      if (occluderMasks && occluderMasks.length > 0 && designW > 0 && designH > 0) {
        const scaleX = sprite.width / nativeW;
        const scaleY = sprite.height / nativeH;
        const worldX = sprite.x + x * scaleX;
        const worldY = sprite.y + y * scaleY;
        const ou = worldX / designW;
        const ov = worldY / designH;
        for (const mask of occluderMasks) {
          if (!mask) continue;
          if (mask.getAlphaAt(ou, ov) >= BACKDROP_ALPHA_MAX) return false;
        }
      }

      return true;
    },
  };
}

/**
 * @typedef {Object} CloudZoneRectNorm
 * @property {number} x  Fraction of design width (0..1).
 * @property {number} y  Fraction of design height -- can be negative
 *                       (legacy uses y=-20 on cows/flowers wide).
 * @property {number} w  Fraction of design width.
 * @property {number} h  Fraction of design height.
 */

/**
 * @typedef {Object} CloudSpawnerHandle
 * @property {() => void} start        Begin the spawn loop (idempotent).
 * @property {() => void} destroy      Cancel all timers + rAFs, remove sprites.
 * @property {() => number} __getActiveCloudCount   Test hook.
 * @property {() => boolean} __isEnabled            Test hook.
 */

/**
 * Build a cloud spawner for a scene.  `themeData` carries the
 * per-theme cloud config loaded from themes.json.  When the theme has
 * no cloud block, the returned handle is a no-op so callers don't
 * have to branch.
 *
 * Textures are preloaded via PIXI.Assets.load in parallel before the
 * spawner is returned; callers should `await` this factory.  The
 * sw precache under vite-plugin-pwa picks up the cloud images via
 * its `**\/*.webp` glob, so on a second visit this round-trip hits
 * the local cache.
 *
 * @param {{
 *   container: PIXI.Container,
 *   themeData: object,
 *   variant: 'standard' | 'wide',
 *   designW: number,
 *   designH: number,
 *   occluderUrls?: string[],
 *   deps?: typeof DEFAULT_DEPS,
 * }} opts
 * @returns {Promise<CloudSpawnerHandle>}
 */
export async function createCloudSpawner({
  container,
  themeData,
  variant,
  designW,
  designH,
  occluderUrls = [],
  deps: depsOverride,
} = {}) {
  const deps = { ...DEFAULT_DEPS, ...(depsOverride || {}) };

  // --- Short-circuit when the theme has no cloud config --------------------
  const imageNames = Array.isArray(themeData?.cloudImageNames)
    ? themeData.cloudImageNames
    : [];
  const variantCfg = themeData?.[variant] || {};
  const zoneNorm = variantCfg.cloudZoneRect || null;
  const cloudSpeed = Number.isFinite(variantCfg.cloudSpeed)
    ? variantCfg.cloudSpeed
    : 0;

  const enabled = imageNames.length > 0
    && zoneNorm !== null
    && cloudSpeed > 0;

  if (!enabled) {
    return {
      start() {},
      destroy() {},
      __getActiveCloudCount() { return 0; },
      __isEnabled() { return false; },
    };
  }

  // --- Denormalize zone into design-pixel coordinates ----------------------
  const zone = {
    x: zoneNorm.x * designW,
    y: zoneNorm.y * designH,
    w: zoneNorm.w * designW,
    h: zoneNorm.h * designH,
  };

  // --- Preload textures ----------------------------------------------------
  // Run loads in parallel via Promise.all.  Individual failures are
  // caught per-texture so one broken asset doesn't poison the set --
  // missing textures are simply skipped when randomly picking.
  const textureByName = new Map();
  await Promise.all(imageNames.map(async (path) => {
    const url = `/${path}`;
    try {
      PIXI.Assets.add({ alias: url, src: url });
      const tex = await PIXI.Assets.load(url);
      textureByName.set(path, tex);
    } catch { /* broken asset -- skip */ }
  }));

  // --- Preload optional lightning-bolt texture ----------------------------
  // Only chicks ships a bolt sprite; cows + flowers have an empty
  // `lightningImageNames` in the plist / themes.json.  We preload the
  // first entry only -- the legacy `getRandomLightningImageFilename`
  // picks uniformly from the list, but every theme's list in the
  // shipping config has exactly one image, so preloading list[0]
  // matches real-world behaviour without complicating this code.
  const lightningNames = Array.isArray(themeData?.lightningImageNames)
    ? themeData.lightningImageNames
    : [];
  let lightningTexture = null;
  if (lightningNames.length > 0) {
    const url = `/${lightningNames[0]}`;
    try {
      PIXI.Assets.add({ alias: url, src: url });
      lightningTexture = await PIXI.Assets.load(url);
    } catch { /* broken asset -- bolt sprite gracefully skipped */ }
  }

  // Probability (0..1) that a cloud tap produces a visible lightning
  // bolt sprite.  Thunder SFX and the screen flash always fire on
  // every tap -- only the bolt is gated by this roll.  Missing /
  // non-finite / >1 values clamp to 1 so the default behaviour
  // matches the pre-config "always show when available" path.
  const lightningBoltChance = (() => {
    const raw = themeData?.lightningBoltChance;
    if (!Number.isFinite(raw)) return 1;
    return Math.max(0, Math.min(1, raw));
  })();

  // Design-space Y where the bolt's bottom edge should land -- the
  // top of the visible background grass for this theme+variant.  The
  // bolt's TOP is pinned to the cloud's center-center; its height is
  // computed so its BOTTOM exactly reaches `grassTopY`.  Missing
  // config falls back to 80% of the design height so a bolt is
  // still reasonably sized even on themes that haven't tuned the
  // value yet.
  const grassTopY = (Number.isFinite(variantCfg.grassTopY)
    ? variantCfg.grassTopY
    : 0.8) * designH;

  // --- Build alpha masks for pixel-perfect hit testing --------------------
  // A tap lands on a cloud only when the cloud's OWN texture is
  // visible at that point AND none of the scene's occluder layers
  // (backdrop + overlay) are opaque there.  Masks are loaded as
  // fresh same-origin Image elements rather than sampled out of
  // PIXI's texture cache -- PIXI v8 may release an ImageBitmap
  // resource after uploading to the GPU, leaving the canvas blank.
  // If a mask fails to build (null), the corresponding check is
  // simply skipped so clouds stay tappable in degraded setups
  // (jsdom, cross-origin taint).
  const safeBuildMask = async (url) => {
    if (!url) return null;
    try { return await deps.buildAlphaMask(url); }
    catch { return null; }
  };

  /** @type {Array<import('./alpha-mask.js').AlphaMask | null>} */
  const occluderMasks = await Promise.all(occluderUrls.map(safeBuildMask));

  /** @type {Map<string, import('./alpha-mask.js').AlphaMask | null>} */
  const cloudMaskByPath = new Map();
  await Promise.all(imageNames.map(async (path) => {
    const mask = await safeBuildMask(`/${path}`);
    cloudMaskByPath.set(path, mask);
  }));

  // --- Spawn state ---------------------------------------------------------
  let destroyed = false;
  let started = false;
  let spawnTimerId = null;
  /** @type {Set<{ sprite: PIXI.Sprite, cancel: { cancelled: boolean } }>} */
  const activeClouds = new Set();

  /**
   * Pointer-tap handler invoked by each cloud sprite's `pointertap`
   * listener.  Mirrors the legacy CloudView's `touchUpInView`
   * → `displayLightning` flow:
   *
   *   1. Fire the side-game telemetry hook (flips
   *      `side_game_engaged` for the current round).
   *   2. Kick off the whole-screen white flash (capped at 1 s
   *      per the legacy BackgroundView `kMaxDuration`).
   *   3. Play a random thunder clip.
   *   4. For chicks only (themes that ship a `lightningImageNames`
   *      entry), attach a bolt sprite as a child of the tapped
   *      cloud until the thunder clip finishes.
   *
   * Idempotent per sprite: tapping a cloud whose bolt is still
   * showing won't stack bolts, but the thunder + flash still fire
   * so the player gets immediate audio feedback.  Matches the
   * legacy behaviour where spam-tapping a cloud plays overlapping
   * thunder clips with a single visible bolt.
   */
  function onCloudTapped(sprite) {
    if (destroyed) return;
    try { deps.onSideGameTap?.(); } catch { /* non-fatal */ }
    // Fire the flash + thunder in parallel; the bolt-sprite
    // overlay is gated on the thunder promise resolving, so it
    // stays visible for the whole clip (2.7–4 s), not the
    // flash's 1 s cap.
    try { deps.triggerScreenFlash?.(); } catch { /* non-fatal */ }

    let thunderDone = null;
    try {
      thunderDone = deps.playThunder ? deps.playThunder() : null;
    } catch { thunderDone = null; }

    // Bolt sprite: only when theme ships one (chicks only per the
    // legacy plist), this cloud isn't already showing its bolt, AND
    // the per-theme probability roll succeeds.  Thunder + flash
    // always fire; only the visible bolt is gated so frequent taps
    // don't always produce a strike.
    const boltRoll = deps.rng();
    if (lightningTexture && !sprite.__boltSprite && boltRoll < lightningBoltChance) {
      const bolt = new PIXI.Sprite(lightningTexture);
      bolt.eventMode = 'none';
      // Size the bolt so its bottom edge lands at the top of the
      // visible grass.  Height = grassTopY - cloudCenterY; width is
      // derived from the texture's natural aspect ratio so the bolt
      // isn't stretched.  A small lower bound guards against clouds
      // whose center already sits below the grass (shouldn't happen
      // with the shipping cloud zones, but if the config changes we
      // still render a visible sliver rather than a 0-px sprite).
      const cloudCenterY = sprite.y + sprite.height / 2;
      const boltH = Math.max(10, grassTopY - cloudCenterY);
      const aspect = (lightningTexture.width || 1) / (lightningTexture.height || 1);
      bolt.height = boltH;
      bolt.width = boltH * aspect;
      positionBoltRelativeTo(bolt, sprite);
      const cloudIdx = typeof container.getChildIndex === 'function'
        ? container.getChildIndex(sprite)
        : container.children.indexOf(sprite);
      if (cloudIdx >= 0 && typeof container.addChildAt === 'function') {
        container.addChildAt(bolt, cloudIdx);
      } else {
        container.addChild(bolt);
      }
      sprite.__boltSprite = bolt;

      const removeBolt = () => {
        if (!sprite.__boltSprite) return;
        const b = sprite.__boltSprite;
        sprite.__boltSprite = null;
        try { b.parent?.removeChild(b); } catch { /* already gone */ }
        try { b.destroy(); } catch { /* already destroyed */ }
      };
      if (thunderDone && typeof thunderDone.then === 'function') {
        thunderDone.then(removeBolt, removeBolt);
      } else {
        // Fallback: no playThunder dep -- remove bolt after the
        // flash's max duration so it doesn't stick forever.
        deps.setTimeoutImpl(removeBolt, 1000);
      }
    }
  }

  /**
   * Place the bolt sprite so its TOP-CENTER aligns with the cloud
   * sprite's CENTER-CENTER.  Cloud width/height here are in
   * display-pixel space (the sprite's `.width/.height`, which equal
   * texture.width/2 at the half-scale we spawn them), so the same
   * formula works during scroll-position updates as the cloud's
   * `.x` / `.y` change frame-to-frame.
   */
  function positionBoltRelativeTo(bolt, sprite) {
    bolt.x = sprite.x + (sprite.width - bolt.width) / 2;
    bolt.y = sprite.y + sprite.height / 2;
  }

  /**
   * Spawn a single cloud.  `randomStart=true` places it at a random
   * X in the middle half of the zone (matches legacy's
   * `spawnCloudStartingOffScreen:NO` branch, used for the first
   * cloud so the sky isn't empty for 30 s after scene start).
   * `randomStart=false` places it off-screen to the right.
   */
  function spawnOne(randomStart) {
    if (destroyed) return;
    // Pick a random (loaded) texture.  Retry-loop caps at imageNames.length
    // attempts to handle the edge case where every texture failed to load.
    let texture = null;
    let pathPicked = null;
    for (let i = 0; i < imageNames.length; i++) {
      const idx = Math.floor(deps.rng() * imageNames.length);
      const path = imageNames[idx];
      const tex = textureByName.get(path);
      if (tex) { texture = tex; pathPicked = path; break; }
    }
    if (!texture) return;

    const sprite = new PIXI.Sprite(texture);
    sprite.__cloudPath = pathPicked;
    // Texture's native pixel size is the @2x~ipad source resolution
    // (~423..996px wide).  CoE's design is 1024 wide, so draw clouds
    // at HALF their native size -- matches the legacy's non-@2x pt
    // scale on an iPad retina display.  Prevents a single cloud from
    // filling half the sky.
    sprite.width = texture.width / 2;
    sprite.height = texture.height / 2;
    // Interactive: receive pointertap for the thunder/lightning reaction.
    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    sprite.on('pointertap', () => onCloudTapped(sprite));

    // Pixel-perfect hit area: reject taps outside the visible cloud
    // silhouette (so the transparent corners of the sprite's rect
    // don't register) and reject taps that land on opaque parts of
    // any occluder layer (backdrop or overlay -- tree, barn, fence
    // -- the cloud is visually hidden behind them).  If masks
    // failed to build, this gracefully degrades to a bounding-box
    // check so clouds are still tappable.
    const cloudMask = cloudMaskByPath.get(pathPicked) ?? null;
    sprite.hitArea = makeCloudHitArea(sprite, cloudMask, occluderMasks, designW, designH);

    // Random Y within the zone.  If the cloud is taller than the
    // zone, we let it overhang the bottom (matches legacy).
    const cloudH = sprite.height;
    const zoneH = Math.max(1, zone.h);
    const maxYOffset = Math.max(1, zoneH - cloudH);
    const yOffset = Math.floor(deps.rng() * maxYOffset);
    sprite.y = zone.y + yOffset;

    // X positioning.  Legacy scrolls right-to-left; start X is either
    // random mid-half OR rightmost edge + cloud width.
    const cloudW = sprite.width;
    const startX = randomStart
      ? zone.x + zone.w * 0.25 + Math.floor(deps.rng() * (zone.w * 0.5))
      : zone.x + zone.w;
    const endX = zone.x - cloudW;
    sprite.x = startX;

    container.addChild(sprite);

    // Scroll duration = distance / speed (design-px per second).
    const durationSec = Math.max(0.1, (startX - endX) / cloudSpeed);
    const cancel = { cancelled: false };
    const record = { sprite, cancel };
    activeClouds.add(record);

    animateOver(durationSec * 1000, (t) => {
      sprite.x = startX + (endX - startX) * t;
      // Keep the bolt's top-center pinned to the cloud's
      // center-center while the cloud scrolls.  Without this
      // sync, a bolt attached near tap time would drift away
      // from the cloud by the end of the ~3 s thunder clip.
      if (sprite.__boltSprite) {
        positionBoltRelativeTo(sprite.__boltSprite, sprite);
      }
    }, cancel)
      .then(() => {
        // Animation done (either reached endX or was cancelled during
        // destroy()).  Remove + destroy the sprite either way.
        activeClouds.delete(record);
        if (sprite.__boltSprite) {
          try { sprite.__boltSprite.parent?.removeChild(sprite.__boltSprite); } catch { /* no-op */ }
          try { sprite.__boltSprite.destroy(); } catch { /* no-op */ }
          sprite.__boltSprite = null;
        }
        try { sprite.parent?.removeChild(sprite); } catch { /* already gone */ }
        try { sprite.destroy(); } catch { /* already destroyed */ }
      });
  }

  /**
   * Compute the delay (in ms) before the next spawn.  Matches legacy
   * CountGameController spawnCloudStartingOffScreen: min is a third
   * of the zone traversal time, jitter is 0..30 seconds.
   */
  function nextSpawnDelayMs() {
    const minSec = (zone.w / 3) / cloudSpeed;
    const jitterSec = Math.floor(deps.rng() * 100) * 0.3;
    return (minSec + jitterSec) * 1000;
  }

  function scheduleNextSpawn() {
    if (destroyed) return;
    const delayMs = nextSpawnDelayMs();
    spawnTimerId = deps.setTimeoutImpl(() => {
      spawnTimerId = null;
      spawnOne(false);
      scheduleNextSpawn();
    }, delayMs);
  }

  function start() {
    if (started || destroyed) return;
    started = true;
    // First cloud fires immediately at a random mid-half start so the
    // sky isn't empty for the first ~30 s.
    spawnOne(true);
    scheduleNextSpawn();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (spawnTimerId !== null) {
      try { deps.clearTimeoutImpl(spawnTimerId); } catch { /* no-op */ }
      spawnTimerId = null;
    }
    // Cancel every in-flight scroll animation.  animateOver's cancel
    // token short-circuits the rAF loop and calls the .then handler
    // which removes the sprite.  We also proactively remove/destroy
    // the sprites here in case the rAF is in a browser tab that's
    // paused (visibilitychange) -- a hidden tab's rAF won't fire until
    // the page is visible again, which could leak sprites if destroy
    // happens during the hidden window.
    for (const { sprite, cancel } of activeClouds) {
      cancel.cancelled = true;
      // Tear down the child bolt sprite first so its texture
      // reference doesn't get dangled if destroy(children:true)
      // below reaps it mid-removal.
      if (sprite.__boltSprite) {
        try { sprite.__boltSprite.parent?.removeChild(sprite.__boltSprite); } catch { /* no-op */ }
        try { sprite.__boltSprite.destroy(); } catch { /* no-op */ }
        sprite.__boltSprite = null;
      }
      try { sprite.parent?.removeChild(sprite); } catch { /* no-op */ }
      try { sprite.destroy(); } catch { /* no-op */ }
    }
    activeClouds.clear();
  }

  return {
    start,
    destroy,
    __getActiveCloudCount() { return activeClouds.size; },
    __isEnabled() { return true; },
  };
}
